/**
 * CompanyMapCallTabs — the model calls behind one saved Company Map, inline
 * in the person panel's Company Map tab: Categories / Assign / Details.
 *
 * A Company Map's calls belong to the run, not to a person: one categories
 * call, then one assign call per batch of titles from the whole list. The
 * Assign tab therefore opens on the batch that carried this person's matched
 * title (found among the rebuilt message's `<title>` elements) and marks it
 * there. A run the worker classified itself keeps no category definitions,
 * so its steps come back `unavailable` (`specs_not_kept`); a run saved from
 * its preview rebuilds.
 * Same read as `CompanyMapPromptModal` (`useCompanyMapPrompt`), so opening
 * the modal from here costs nothing more.
 */
import { ProgressBar } from "@nu-assist/ui";
import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";

import type { BctClient, BctPromptResponse, BctPromptStep } from "../../clients/bct.client";
import { Kicker, Pre } from "../ai-review-prompt/viewerParts";
import { fmtCost, fmtCount, fmtSeconds, modelLabel } from "../person-panel/format";
import { StepNote, UNAVAILABLE_TEXT, useCompanyMapPrompt } from "./CompanyMapPromptModal";

type CallTab = "categories" | "assign" | "details";
const TABS: readonly CallTab[] = ["categories", "assign", "details"];
const TAB_LABEL: Record<CallTab, string> = {
  categories: "Categories",
  assign: "Assign",
  details: "Details",
};

/** A title as the classifier writes it into a message (`_one_line`:
 *  whitespace runs collapsed), compared case-insensitively. */
function normTitle(title: string): string {
  return title.split(/\s+/).filter(Boolean).join(" ").toLowerCase();
}

/** `<title id="N">…</title>` — the only place a batch's own titles appear.
 *  The roles and category blocks before them are the same in every batch and
 *  name the tracked roles ("CFO"), so a plain-text search would match them. */
const TITLE_ELEMENT = /(<title id="\d+">)([^<]*)(<\/title>)/g;

function batchTitles(message: string | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const m of (message ?? "").matchAll(TITLE_ELEMENT)) out.add(normTitle(m[2] ?? ""));
  return out;
}

/** 0-based index into `assign_batches` of the first batch whose
 *  `<job_titles>` carries one of `titles`; 0 when none does. */
export function batchForTitles(prompt: BctPromptResponse, titles: readonly string[]): number {
  const wanted = titles.map(normTitle).filter(Boolean);
  if (wanted.length === 0) return 0;
  const i = prompt.assign_batches.findIndex((b) => {
    const have = batchTitles(b.user_message);
    return wanted.some((t) => have.has(t));
  });
  return i < 0 ? 0 : i;
}

/** `text` with the `<title>` elements that carry one of `titles` marked. */
function Marked({ text, titles }: { text: string; titles: readonly string[] }) {
  const wanted = new Set(titles.map(normTitle).filter(Boolean));
  if (wanted.size === 0) return <>{text}</>;
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(TITLE_ELEMENT)) {
    const [whole, open = "", title = "", close = ""] = m;
    const at = m.index ?? 0;
    if (!wanted.has(normTitle(title))) continue;
    parts.push(<span key={`t${last}`}>{text.slice(last, at)}</span>);
    parts.push(
      <span key={`o${at}`}>
        {open}
        <mark data-testid="cmp-inline-title-mark" className="rounded-sm bg-mark text-mark-ink">
          {title}
        </mark>
        {close}
      </span>
    );
    last = at + whole.length;
  }
  parts.push(<span key={`t${last}`}>{text.slice(last)}</span>);
  return <>{parts}</>;
}

/** A labelled part of a call (system prompt, tool). */
function Section({
  label,
  testId,
  children,
}: { label: string; testId: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <Kicker>{label}</Kicker>
      {children}
    </div>
  );
}

/** One call: its note (mismatch / no call), the message, then the system
 *  prompt and the tool below it. */
function StepBody({
  step,
  index,
  titles,
  messageTestId,
}: {
  step: BctPromptStep;
  index: number;
  titles: readonly string[];
  messageTestId: string;
}) {
  if (step.status === "unavailable") {
    return (
      <p
        data-testid="cmp-inline-unavailable"
        className="rounded-md border border-border bg-bg-subtle px-4 py-6 text-center text-body text-text-secondary"
      >
        {step.reason ? UNAVAILABLE_TEXT[step.reason] : "This prompt cannot be shown."}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <StepNote step={step} index={index} />
      <Pre testId={messageTestId}>
        <Marked text={step.user_message ?? ""} titles={titles} />
      </Pre>
      <Section label="System prompt" testId="cmp-inline-system">
        <Pre testId="cmp-inline-system-text">{step.system ?? ""}</Pre>
      </Section>
      <Section label="Tool" testId="cmp-inline-tool">
        <Pre testId="cmp-inline-tool-text">{JSON.stringify(step.tool_config ?? null, null, 2)}</Pre>
      </Section>
    </div>
  );
}

function DetailsPanel({ prompt }: { prompt: BctPromptResponse }) {
  const steps = [prompt.categories_step, ...prompt.assign_batches];
  return (
    <div className="flex flex-col gap-3" data-testid="cmp-inline-details">
      <p className="text-caption text-text-secondary">
        {modelLabel(prompt.model_id)}
        {prompt.prompt_version && ` · Company Map prompt ${prompt.prompt_version}`}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-caption">
          <thead className="text-micro uppercase tracking-wide text-text-muted">
            <tr>
              <th className="py-1 pr-3 font-medium">Call</th>
              <th className="py-1 pr-3 font-medium">Status</th>
              <th className="py-1 pr-3 font-medium">Tokens</th>
              <th className="py-1 pr-3 font-medium">Time</th>
              <th className="py-1 pr-3 font-medium">Cost</th>
              <th className="py-1 font-medium">Attempts</th>
            </tr>
          </thead>
          <tbody className="text-text">
            {steps.map((s, i) => (
              <tr
                key={i === 0 ? "categories" : `batch-${i}`}
                className="border-t border-border"
                data-testid="cmp-inline-details-row"
              >
                <td className="py-1 pr-3">
                  {i === 0 ? "Categories" : `Batch ${i} · ${fmtCount(s.title_count)} titles`}
                </td>
                <td className="py-1 pr-3">{s.status.replace("_", " ")}</td>
                <td className="py-1 pr-3 tabular-nums">
                  {s.call?.input_tokens != null && s.call.output_tokens != null
                    ? `${fmtCount(s.call.input_tokens)} in · ${fmtCount(s.call.output_tokens)} out`
                    : "—"}
                </td>
                <td className="py-1 pr-3 tabular-nums">
                  {s.call?.latency_ms_client != null ? fmtSeconds(s.call.latency_ms_client) : "—"}
                </td>
                <td className="py-1 pr-3 tabular-nums">
                  {s.call?.cost_usd_est != null ? fmtCost(s.call.cost_usd_est) : "—"}
                </td>
                <td className="py-1 tabular-nums">{s.call?.attempts?.length ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CompanyMapCallTabs({
  client,
  runId,
  titles,
  onBatchChange,
}: {
  client: BctClient;
  runId: string;
  /** The person's matched job titles: pick and mark the batch that carried them. */
  titles: readonly string[];
  /** The assign batch shown (0-based; null with no batch), chosen or picked,
   *  for "Open full screen" to open on it. */
  onBatchChange?: (batch: number | null) => void;
}) {
  const tabsId = useId();
  const [tab, setTab] = useState<CallTab>("assign");
  const [batch, setBatch] = useState<number | null>(null);
  const tabRefs = useRef<Partial<Record<CallTab, HTMLButtonElement | null>>>({});
  const query = useCompanyMapPrompt(client, { kind: "run", id: runId });
  const prompt = query.data;
  const selected = batch ?? (prompt ? batchForTitles(prompt, titles) : 0);
  const hasBatch = (prompt?.assign_batches.length ?? 0) > 0;
  const reportBatch = useRef(onBatchChange);
  reportBatch.current = onBatchChange;
  useEffect(() => {
    if (prompt) reportBatch.current?.(hasBatch ? selected : null);
  }, [prompt, hasBatch, selected]);

  const onTabKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const next =
      TABS[(TABS.indexOf(tab) + (e.key === "ArrowRight" ? 1 : TABS.length - 1)) % TABS.length];
    if (!next) return;
    setTab(next);
    tabRefs.current[next]?.focus();
  };

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-2 py-6" data-testid="cmp-inline-loading">
        <ProgressBar indeterminate live label="Rebuilding the prompts" />
        <p className="text-caption text-text-muted">Rebuilding the prompts…</p>
      </div>
    );
  }
  if (query.isError || !prompt) {
    return (
      <p
        data-testid="cmp-inline-error"
        className="rounded-md border border-border bg-bg-subtle px-4 py-6 text-center text-body text-text-secondary"
      >
        {query.error?.status === 404
          ? "This Company Map's model calls are no longer available."
          : "The prompts could not be loaded. Try again in a moment."}
      </p>
    );
  }

  const step = prompt.assign_batches[selected];

  return (
    <div className="flex min-h-0 flex-col gap-3" data-testid="cmp-inline-tabs">
      <div
        role="tablist"
        aria-label="Company Map model calls"
        className="flex gap-1 overflow-x-auto border-b border-border"
        onKeyDown={onTabKeyDown}
      >
        {TABS.map((t) => (
          <button
            key={t}
            ref={(el) => {
              tabRefs.current[t] = el;
            }}
            type="button"
            role="tab"
            id={`${tabsId}-tab-${t}`}
            aria-selected={tab === t}
            aria-controls={`${tabsId}-panel`}
            tabIndex={tab === t ? 0 : -1}
            data-testid={`cmp-inline-tab-${t}`}
            onClick={() => setTab(t)}
            className={`whitespace-nowrap border-b-2 px-2 py-2 text-label focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
              tab === t
                ? "border-accent text-accent-ink"
                : "border-transparent text-text-secondary hover:text-text"
            }`}
          >
            {t === "assign" && prompt.assign_batches.length > 0
              ? `${TAB_LABEL[t]} · ${prompt.assign_batches.length}`
              : TAB_LABEL[t]}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id={`${tabsId}-panel`}
        aria-labelledby={`${tabsId}-tab-${tab}`}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scroll region must be focusable to scroll by keyboard
        tabIndex={0}
        className="max-h-96 min-h-0 overflow-auto rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        {tab === "categories" && (
          <StepBody
            step={prompt.categories_step}
            index={0}
            titles={[]}
            messageTestId="cmp-inline-categories-message"
          />
        )}
        {tab === "assign" &&
          (step ? (
            <div className="flex flex-col gap-3">
              {prompt.assign_batches.length > 1 && (
                <div className="flex flex-wrap items-center gap-1">
                  <Kicker>Batch</Kicker>
                  {prompt.assign_batches.map((b, i) => (
                    <button
                      // biome-ignore lint/suspicious/noArrayIndexKey: batches are positional
                      key={i}
                      type="button"
                      data-testid={`cmp-inline-batch-${i + 1}`}
                      aria-pressed={i === selected}
                      title={`${fmtCount(b.title_count)} titles`}
                      onClick={() => setBatch(i)}
                      className={`rounded-pill border px-2 py-0.5 text-caption focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                        i === selected
                          ? "border-accent bg-accent-muted text-accent-ink"
                          : "border-border text-text-secondary hover:text-text"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-caption text-text-secondary" data-testid="cmp-inline-batch-label">
                Batch {selected + 1} of {prompt.assign_batches.length} ·{" "}
                {fmtCount(step.title_count)} titles
              </p>
              <StepBody
                step={step}
                index={selected + 1}
                titles={titles}
                messageTestId="cmp-inline-assign-message"
              />
            </div>
          ) : (
            <p className="text-body text-text-secondary" data-testid="cmp-inline-no-batches">
              No job title was in scope, so no assign call was made.
            </p>
          ))}
        {tab === "details" && <DetailsPanel prompt={prompt} />}
      </div>
    </div>
  );
}
